import { bool, date, number, object, shape, string } from 'prop-types'
import React, { Fragment } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import DoxButton from '../../Shared/components/DoxButton'
import EmDash from '../../Shared/components/EmDash'
import { useTranslationContext } from '../../Shared/hooks/useTranslationContextHook'
import { ErrorTypes } from '../../Shared/resources/Enums'
import DoxCheckMark from '../graphics/doxCheckMark'
import { useFileUpload } from '../hooks/useFileUploadHook'
import DoxProgressBar from './doxProgressBar'

const DoxUploadItem = ({ queuedFile, cancel, layoutType, isSignaturePageContext = false, filerName = null }) => {

    // gah, just do this once here
    const fileName = ((queuedFile || {}).file || {}).name

    const translateString = useTranslationContext()

    const {
        percentUploaded, isUploadComplete, uploadError, renameFileAs,
        handleCancelClick, handleRetryClick, handleKeepBothClick, handleReplaceClick } = useFileUpload(queuedFile, cancel, layoutType, isSignaturePageContext, filerName)

    const errorsWithRetryAndCancel = [
        ErrorTypes.UNKNOWN_ERROR,
        ErrorTypes.INVALID_FILE_TYPE,
        ErrorTypes.FILE_TOO_BIG
    ]

    const shouldShowProgressBar = () => !uploadError || errorsWithRetryAndCancel.includes(uploadError)

    const shouldShowConflictResolutionOptions = () => uploadError === ErrorTypes.FILE_EXISTS

    const renderErrorMessage = () => {
        const translatedError = translateString(`Dox.DocumentUploader.ValidationErrors.${uploadError}`)

        if (uploadError !== ErrorTypes.INVALID_FILE_TYPE || !translatedError) {
            return translatedError
        }

        const separatorIndex = translatedError.indexOf(':')
        if (separatorIndex < 0) {
            return translatedError
        }

        const label = translatedError.slice(0, separatorIndex + 1)
        const value = translatedError.slice(separatorIndex + 1).trim()

        return (
            <>
                <strong>{label}</strong>
                {' '}
                <span className='error-message-light'>{value}</span>
            </>
        )
    }

    return (
        <div className='dox-upload-item'>
            <div className='hrb-columns'>
                <div className='hrb-column hrb-is-8'>
                    <p className={(uploadError || shouldShowConflictResolutionOptions()) ? 'error': 'filename' }>
                        {renameFileAs ? renameFileAs : (fileName ? fileName : <EmDash />)}
                    </p>
                </div>

                <div className='hrb-column hrb-is-4 flex-end'>
                    {shouldShowProgressBar() && !shouldShowConflictResolutionOptions() &&
                        <DoxProgressBar percent={percentUploaded} hasError={!!uploadError || percentUploaded === 0} showPercentage />
                    }
                </div>
                {shouldShowConflictResolutionOptions() &&
                    <Fragment>
                        <div className='hrb-column hrb-is-2'></div>
                        <div className='hrb-column'>
                            <DoxButton
                                elementId={`keepboth-upload-${fileName}`}
                                buttonStyles='link-style no-underline force-100'
                                labelKey='Dox.DocumentUploader.CTAs.KeepBoth'
                                onClick={handleKeepBothClick} />
                        </div>
                        <div className='hrb-column'>
                            <DoxButton
                                elementId={`replace-upload-${fileName}`}
                                buttonStyles='link-style no-underline force-100'
                                labelKey='Dox.DocumentUploader.CTAs.Replace'
                                onClick={handleReplaceClick} />
                        </div>                   
                    </Fragment>
                }

                {errorsWithRetryAndCancel.includes(uploadError) &&
                    <div className='hrb-column'>
                        <DoxButton
                            elementId={`retry-upload-${fileName}`}
                            buttonStyles='link-style no-underline force-100'
                            labelKey='Dox.DocumentUploader.CTAs.Retry'
                            onClick={handleRetryClick} />
                    </div>
                }

                {(!uploadError || errorsWithRetryAndCancel.includes(uploadError) || uploadError === ErrorTypes.FILE_EXISTS) &&
                    <div className='hrb-column centered'>
                        {isUploadComplete && !uploadError ?
                            <div className='checkmark'>
                                <DoxCheckMark />
                            </div>
                            :
                            (percentUploaded >= 100 && !isUploadComplete ?
                                <div className='spinner'>
                                    <FontAwesomeIcon icon={faSpinner} className='fa-pulse' />
                                </div>
                                :
                                <DoxButton
                                    elementId={`cancel-upload-${fileName}`}
                                    buttonStyles='link-style no-underline force-100'
                                    labelKey='Dox.DocumentUploader.CTAs.Cancel'
                                onClick={handleCancelClick} />                            
                            )}
                    </div>
                }
            </div>
            <div className='hrb-columns'>
                {(uploadError || shouldShowConflictResolutionOptions()) &&
                    <div className='hrb-column hrb-is-6'>
                        <p className='error-message'>{renderErrorMessage()}</p>
                    </div>
                }                
            </div>
        </div>
    )
}

DoxUploadItem.propTypes = {
    queuedFile: shape({
        file: shape({
            lastModified: number,
            lastModifiedDate: object,
            name: string,
            size: number,
            type: string
        }),
        qPosition: number,
        readyToUpload: bool,
        isUploadComplete: bool
    }).isRequired,
    cancel: bool,
    layoutType: string,
    isSignaturePageContext: bool,
    filerName: string
}

export default DoxUploadItem